// import 'expo/build/Expo.fx';
// Sentry init runs at module load. Wrap so an unlinked native module (older
// binaries shipped before @sentry/react-native was added) can't crash the JS bundle.
try {
  require('./common/sentry');
} catch (error) {
  console.warn('Sentry init skipped', error);
}
import React, { lazy, Suspense, useEffect } from 'react';
import { Platform } from 'react-native';
import { registerRootComponent } from 'expo';
import { createRoot } from 'react-dom/client';
import { createElement } from 'react';

import api from './api/api';
import ActivityIndicatorScreen from './components/ActivityIndicatorScreen';
import AppLazy from './AppLazy';
import environment from './environments';
import useCachedResources from './hooks/app/useCachedResources';
import {
  disableOrientationLock,
  isTablet,
  enablePortraitOrientationLock
} from './tools/DeviceHelper';

// LogBox.ignoreAllLogs(true);
api.setEnvironment(environment);

// // appcues web setup
// declare global {
//   interface Window {
//     Appcues: any;
//     AppcuesSettings: any;
//   }
// }
// const injectAppcuesWebScript = () => {
//   console.log('injectAppcuesWebScript');
//   const scriptType = document.createElement('script');
//   scriptType.type = 'text/javascript';
//   scriptType.textContent =
//     'window.AppcuesSettings = { enableURLDetection: true };';
//   const script = document.createElement('script');
//   script.src = `//fast.appcues.com/${environment.appcuesAccountId}.js`;
//   document.head.append(scriptType);
//   document.head.append(script);
// };

const injectWebCss = () => {
  const style = document.createElement('style');
  style.textContent = `textarea, select, input, button { outline: none !important; }\n.grecaptcha-badge { visibility: hidden; }`;
  return document.head.append(style);
};

const App: React.FC = () => {
  const isLoadingComplete = useCachedResources();

  useEffect(() => {
    if (Platform.OS === 'web') {
      injectWebCss();
    } else {
      updateOrientationLock();
    }
  }, []);

  if (!isLoadingComplete) {
    return null;
  }

  // On native we always render AppLazy directly. lazy()+dynamic-import was
  // originally for web code-splitting, but React 19 turns any module-eval
  // failure in the lazy chain into a fatal "resolves to: undefined" crash.
  if (Platform.OS !== 'web') {
    return <AppLazy />;
  }

  const isWebEmbed = window.location.pathname.includes('/web-embed');
  const isWebStudyQualificationEmbed =
    window.location.pathname.includes('/qualify');
  const AppComponent = lazy(() =>
    isWebEmbed
      ? import('./web-embed/WebEmbed')
      : isWebStudyQualificationEmbed
      ? import('./AppWebQualification')
      : import('./AppLazy')
  );

  return (
    <Suspense fallback={<ActivityIndicatorScreen />}>
      <AppComponent />
    </Suspense>
  );
};

async function updateOrientationLock() {
  if (await isTablet()) {
    await disableOrientationLock();
  } else {
    await enablePortraitOrientationLock();
  }
}

// @see https://github.com/expo/expo/issues/18485
if (Platform.OS === 'web') {
  const rootTag = createRoot(
    document.getElementById('root') ?? document.getElementById('main')!
  );
  rootTag.render(createElement(App));
} else {
  registerRootComponent(App);
}
